import React, { FC, FormEvent, useEffect, useRef, useState } from 'react';
import { IWritableChip } from 'core/interface/writableChip';
import Image from 'components/image/Image';

import './Index.scss';

const arraysEqual = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const WritableChip: FC<IWritableChip> = ({
  onSelectedValues,
  className,
  inputClassName,
  placeholder,
  defaultValues,
  setTouch
}): JSX.Element => {
  const [values, setValues] = useState<string[]>(() =>
    Array.isArray(defaultValues) ? defaultValues : []
  );
  const [typedValue, setTypedValue] = useState<string>('');
  const onSelectedValuesRef = useRef(onSelectedValues);

  useEffect(() => {
    onSelectedValuesRef.current = onSelectedValues;
  }, [onSelectedValues]);

  useEffect(() => {
    onSelectedValuesRef.current(values);
  }, [values]);

  useEffect(() => {
    if (Array.isArray(defaultValues)) {
      setValues((prev) =>
        arraysEqual(prev, defaultValues) ? prev : defaultValues
      );
      return;
    }
    if (defaultValues == null || defaultValues === '') {
      setValues((prev) => (prev.length ? [] : prev));
    }
  }, [defaultValues]);

  const handleInput = (e: FormEvent<HTMLInputElement>): void => {
    const value = (e.target as HTMLInputElement).value;
    if ((e.nativeEvent as InputEvent).inputType !== 'insertLineBreak') {
      setTypedValue(value);
    }
  };

  const handleRemove = (value: string): void => {
    setValues((prevValues) => prevValues.filter((v) => v !== value));
    setTouch?.();
  };

  const commitTypedValue = (): void => {
    const pending = typedValue.trim();
    if (!pending) return;
    setValues((prevValues) =>
      prevValues.includes(pending) ? prevValues : [...prevValues, pending]
    );
    setTypedValue('');
    setTouch?.();
  };

  return (
    <div className={`wchip ${className ?? ''}`}>
      <input
        value={typedValue}
        placeholder={placeholder}
        className={`${inputClassName ?? ''} input`}
        type="text"
        onInput={(e) => handleInput(e)}
        onBlur={commitTypedValue}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.code === 'Enter') {
            e.preventDefault();
            commitTypedValue();
          }
        }}
      />
      {values?.map((value, index) => (
        <span className="chip-selected" key={`${value}-${index}`}>
          {value}{' '}
          <span onClick={() => handleRemove(value)}>
            <Image
              className="remove"
              name="removes.svg"
              width={10}
              height={10}
            />
          </span>
        </span>
      ))}
    </div>
  );
};

export default WritableChip;
